import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Upload, Eye, EyeOff, AlertCircle } from 'lucide-react';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoError, setLogoError] = useState(false);
  const { login, branding } = useAuth();
  const navigate = useNavigate();

  const logoUrl = branding.logo_url && !logoError
    ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`
    : null;

  const primaryColor = branding.primary_color || '#DC2626';
  
  const backgroundImageUrl = branding.background_image_url
    ? `${process.env.REACT_APP_BACKEND_URL}${branding.background_image_url}`
    : 'https://images.unsplash.com/photo-1762279388956-1c098163a2a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBkYXRhJTIwZmxvdyUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3MjQ4Nzg5NHww&ixlib=rb-4.1.0&q=85';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const user = await login(username, password);
      toast.success('Welcome back!');
      navigate(user.role === 'admin' ? '/admin' : '/upload');
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid credentials';
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page" style={{ backgroundColor: branding.background_color || '#09090B' }}>
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${backgroundImageUrl})`
          }}
        />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex flex-col items-center text-center">
            <div className="flex items-center gap-3 mb-4">
              {logoUrl ? (
                <img 
                  src={logoUrl}
                  alt="Logo"
                  className="h-28 w-auto object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <div className="w-20 h-20 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                  <Upload className="w-10 h-10 text-white" />
                </div>
              )}
            </div>
            <h1 className="font-display text-3xl text-white tracking-tight">
              {branding.site_title || 'NOW Upload Portal'}
            </h1>
            <p className="text-zinc-400 text-lg max-w-md mt-2">
              NOW Television Network
            </p>
          </div>
          
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-zinc-800/80 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-red-500 font-bold">01</span>
              </div>
              <div>
                <h3 className="text-white font-medium">Drag & Drop Upload</h3>
                <p className="text-zinc-500 text-sm">Upload large video files with ease</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-zinc-800/80 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-red-500 font-bold">02</span>
              </div>
              <div>
                <h3 className="text-white font-medium">Resume Support</h3>
                <p className="text-zinc-500 text-sm">Continue uploads after connection drops</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-zinc-800/80 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-red-500 font-bold">03</span>
              </div>
              <div>
                <h3 className="text-white font-medium">Secure Transfer</h3>
                <p className="text-zinc-500 text-sm">Your files are encrypted and protected</p>
              </div>
            </div>
          </div>
          
          <p className="text-zinc-600 text-sm">
            &copy; {new Date().getFullYear()} NOW Television Network. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8" style={{ backgroundColor: branding.background_color || '#09090B' }}>
        <div className="w-full max-w-md space-y-6 sm:space-y-8 animate-fadeIn">
          {/* Mobile Logo - Only visible on small screens */}
          <div className="flex lg:hidden flex-col items-center mb-6">
            {logoUrl ? (
              <img 
                src={logoUrl}
                alt="Logo"
                className="h-20 w-auto object-contain mb-2"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="w-16 h-16 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: primaryColor }}>
                <Upload className="w-8 h-8 text-white" />
              </div>
            )}
            <p className="text-zinc-400 text-sm">NOW Television Network</p>
          </div>
          
          <div className="text-left">
            <h2 className="font-display text-3xl sm:text-4xl text-white tracking-wider font-bold">SIGN IN</h2>
            <p className="mt-2 text-zinc-500 text-sm sm:text-base">Enter your credentials to access the portal</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="login-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="username" className="text-zinc-400 uppercase text-xs tracking-wider">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                className="h-12 bg-zinc-900 border-zinc-800 rounded-lg text-white placeholder:text-zinc-600 focus:border-red-600 focus:ring-red-600/20"
                required
                autoComplete="username"
                data-testid="username-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-400 uppercase text-xs tracking-wider">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="h-12 bg-zinc-900 border-zinc-800 rounded-lg text-white placeholder:text-zinc-600 focus:border-red-600 focus:ring-red-600/20 pr-12"
                  required
                  autoComplete="current-password"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 rounded-lg text-white font-medium uppercase tracking-wider btn-glow transition-all duration-200"
              style={{ backgroundColor: primaryColor }}
              data-testid="login-submit"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </div>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <p className="text-center text-zinc-600 text-xs sm:text-sm">
            Contact <a href="mailto:traffic@thenownetwork.org" className="text-red-500 hover:text-red-400 underline">traffic@thenownetwork.org</a> if you need access
          </p>
          
          {/* Mobile Copyright */}
          <p className="lg:hidden text-center text-zinc-700 text-xs mt-4">
            &copy; {new Date().getFullYear()} NOW Television Network
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
